import React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useLanguage } from '../hooks/useLanguage';

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact' | 'icon-only';
  showLabel?: boolean;
  whiteTheme?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'default',
  showLabel = true,
  whiteTheme = false
}) => {
  const { currentLanguage, languages, changeLanguage, t } = useLanguage();

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value;
    changeLanguage(newLanguage);
  };

  if (variant === 'icon-only') {
    return (
      <FormControl variant="outlined" size="small" sx={{ minWidth: 90 }}>
        <Select
          value={currentLanguage}
          onChange={handleLanguageChange}
          displayEmpty
          aria-label="Select language"
          inputProps={{
            'aria-label': 'Language selection',
          }}
          renderValue={(value) => (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              bgcolor: whiteTheme ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 1,
              px: 1,
              py: 0.5
            }}>
              <LanguageIcon 
                fontSize="small" 
                sx={{ color: whiteTheme ? 'white' : 'inherit' }}
              />
              <Typography variant="body2" sx={{ 
                textTransform: 'uppercase',
                fontWeight: 600,
                color: whiteTheme ? 'white' : 'inherit'
              }}>
                {value}
              </Typography>
            </Box>
          )}
          sx={{
            '.MuiOutlinedInput-notchedOutline': { border: 0 },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 1,
            },
          }}
        >
          {languages.map((language) => (
            <MenuItem key={language.code} value={language.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="body2" sx={{ 
                  textTransform: 'uppercase', 
                  minWidth: 24,
                  fontWeight: language.code === currentLanguage ? 600 : 400,
                  color: language.code === currentLanguage ? 'primary.main' : 'inherit'
                }}>
                  {language.code}
                </Typography>
                <Typography variant="body2" sx={{
                  fontWeight: language.code === currentLanguage ? 600 : 400,
                  color: language.code === currentLanguage ? 'primary.main' : 'inherit'
                }}>
                  {language.nativeName}
                </Typography>
                {language.code === currentLanguage && (
                  <Typography variant="body2" sx={{ ml: 'auto', color: 'primary.main' }}>
                    ✓
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (variant === 'compact') {
    const currentLang = languages.find(lang => lang.code === currentLanguage);
    
    return (
      <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
        <Select
          value={currentLanguage}
          onChange={handleLanguageChange}
          displayEmpty
          aria-label="Select language"
          inputProps={{
            'aria-label': 'Language selection',
          }}
          renderValue={(value) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {currentLang?.nativeName || value.toUpperCase()}
              </Typography>
            </Box>
          )}
        >
          {languages.map((language) => (
            <MenuItem key={language.code} value={language.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="body2" sx={{ 
                  textTransform: 'uppercase', 
                  minWidth: 24,
                  fontWeight: language.code === currentLanguage ? 600 : 400,
                  color: language.code === currentLanguage ? 'primary.main' : 'inherit'
                }}>
                  {language.code}
                </Typography>
                <Typography variant="body2" sx={{
                  fontWeight: language.code === currentLanguage ? 600 : 400,
                  color: language.code === currentLanguage ? 'primary.main' : 'inherit'
                }}>
                  {language.nativeName}
                </Typography>
                {language.code === currentLanguage && (
                  <Typography variant="body2" sx={{ ml: 'auto', color: 'primary.main' }}>
                    ✓
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Default variant
  return (
    <FormControl fullWidth variant="outlined">
      {showLabel && (
        <InputLabel id="language-select-label">
          {t('language.select')}
        </InputLabel>
      )}
      <Select
        labelId="language-select-label"
        value={currentLanguage}
        onChange={handleLanguageChange}
        label={showLabel ? t('language.select') : undefined}
        startAdornment={
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            <LanguageIcon color="action" />
          </Box>
        }
      >
        {languages.map((language) => (
          <MenuItem key={language.code} value={language.code}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography variant="body2" sx={{ 
                textTransform: 'uppercase', 
                fontWeight: 'bold',
                minWidth: 30,
                textAlign: 'center'
              }}>
                {language.code}
              </Typography>
              <Box>
                <Typography variant="body1">
                  {language.nativeName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {language.name}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSwitcher;